
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FileText, Database } from "lucide-react";
import { useAnalyticsExport } from "../hooks/useAnalyticsExport";
import type { Candidate } from "@/components/candidates/types";

interface ExportDialogProps {
  candidates: Candidate[];
  dateRange?: { from: Date; to: Date };
}

export const ExportDialog = ({ candidates, dateRange }: ExportDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const { exportData, isExporting } = useAnalyticsExport();

  const handleExport = () => {
    exportData({
      candidates,
      dateRange,
      format: exportFormat
    });
    setIsOpen(false);
  };

  const formatOptions = [
    {
      value: 'csv' as const,
      label: 'CSV (Spreadsheet)',
      description: 'Export as comma-separated values for Excel/Google Sheets',
      icon: <Database className="h-4 w-4" />
    },
    {
      value: 'pdf' as const,
      label: 'JSON Report',
      description: 'Export as structured data report',
      icon: <FileText className="h-4 w-4" />
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          {isExporting ? "Exporting..." : "Export"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export Analytics Data</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <Label className="text-base font-medium">Export Format</Label>
            <RadioGroup
              value={exportFormat}
              onValueChange={(value: 'csv' | 'pdf') => setExportFormat(value)}
              className="space-y-3"
            >
              {formatOptions.map((option) => (
                <div key={option.value} className="flex items-start space-x-3">
                  <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      {option.icon}
                      <Label htmlFor={option.value} className="font-medium cursor-pointer">
                        {option.label}
                      </Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="text-base font-medium">Export Options</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-metadata"
                checked={includeMetadata}
                onCheckedChange={(checked) => setIncludeMetadata(checked as boolean)}
              />
              <Label htmlFor="include-metadata" className="text-sm cursor-pointer">
                Include metadata and summary statistics
              </Label>
            </div>
          </div>

          <div className="bg-muted/30 p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Export includes:</strong> {candidates.length} candidates
              {dateRange && (
                <span> from {dateRange.from.toLocaleDateString()} to {dateRange.to.toLocaleDateString()}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? "Exporting..." : "Export Data"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
